import { useId, useState, type ReactNode } from 'react';
import { CircleAlert, Eye, EyeOff } from 'lucide-react';

/**
 * Campos de captura pensados para el teléfono.
 *
 * Detalles que importan en iPhone:
 *   - `inputMode` elige el teclado: numérico o decimal (Req. 5.9)
 *   - la fuente de 16 px evita que iOS acerque la pantalla al enfocar (Req. 8.4)
 *   - la altura mínima cumple el área táctil de 44 puntos (Req. 8.1)
 *   - la etiqueta siempre es visible y el error se anuncia con
 *     aria-describedby y texto, no solo con color (Req. 8.5)
 *
 * Origen: Control de Negocio de Comida, sin el campo de dinero. El campo con
 * selector de unidad (kg | lb, cm | in) está en UnitField.tsx.
 */

// Campo de Foco: 48 px de alto, esquinas de 13 px y texto de 16 px. El borde
// usa `borde-fuerte` (3:1) para que el campo se distinga del fondo.
const CLASES_ENTRADA =
  'w-full min-h-12 rounded-[13px] border bg-superficie px-3 py-[11px] text-base text-texto ' +
  'placeholder:text-texto-suave disabled:bg-superficie-suave disabled:text-texto-suave';

export function clasesCon(error: boolean): string {
  return `${CLASES_ENTRADA} ${error ? 'border-alerta' : 'border-borde-fuerte'}`;
}

/** Deja solo dígitos y un separador decimal; la coma se vuelve punto (Req. 5.9). */
export function limpiarDecimal(texto: string): string {
  const soloValidos = texto.replace(/[^0-9.,]/g, '').replace(',', '.');
  const [entero = '', ...resto] = soloValidos.split('.');
  return resto.length === 0 ? entero : `${entero}.${resto.join('').replace(/,/g, '')}`;
}

interface Envoltura {
  etiqueta: string;
  /**
   * Lo que completa la etiqueta solo para el lector de pantalla, cuando hay
   * varios campos iguales en la pantalla: "Cantidad" + "de Wellthy".
   */
  complemento?: string;
  ayuda?: string;
  error?: string | null;
  /** Texto adicional asociado al campo, como una equivalencia en vivo. */
  extra?: ReactNode;
  children: (props: { id: string; describedBy: string | undefined; hayError: boolean }) => ReactNode;
}

export function Campo({ etiqueta, complemento, ayuda, error, extra, children }: Envoltura) {
  const id = useId();
  const idAyuda = `${id}-ayuda`;
  const idError = `${id}-error`;
  const hayError = error !== null && error !== undefined && error !== '';

  const idExtra = `${id}-extra`;
  const hayExtra = extra !== undefined && extra !== null && extra !== '';

  const describedBy =
    [hayExtra ? idExtra : null, hayError ? idError : null, ayuda !== undefined ? idAyuda : null]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <div className="grid min-w-0 gap-[7px]">
      <label htmlFor={id} className="block text-[13px] text-texto-suave">
        {etiqueta}
        {complemento !== undefined && <span className="sr-only"> {complemento}</span>}
      </label>
      {children({ id, describedBy, hayError })}
      {hayExtra && (
        <p id={idExtra} className="text-[13px] font-medium tabular-nums text-marca">
          {extra}
        </p>
      )}
      {ayuda !== undefined && !hayError && (
        <p id={idAyuda} className="text-[12px] text-texto-suave">
          {ayuda}
        </p>
      )}
      {hayError && (
        <p id={idError} className="flex items-start gap-1.5 text-[12px] font-medium text-alerta">
          <CircleAlert className="mt-px size-3.5" />
          {error}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PropsTexto {
  etiqueta: string;
  valor: string;
  onCambio: (valor: string) => void;
  ayuda?: string;
  error?: string | null;
  placeholder?: string;
  autoFocus?: boolean;
  maxLength?: number;
  lista?: readonly string[];
}

export function TextField({
  etiqueta,
  valor,
  onCambio,
  ayuda,
  error,
  placeholder,
  autoFocus,
  maxLength,
  lista,
}: PropsTexto) {
  const idLista = useId();

  return (
    <Campo etiqueta={etiqueta} ayuda={ayuda} error={error}>
      {({ id, describedBy, hayError }) => (
        <>
          <input
            id={id}
            type="text"
            value={valor}
            onChange={(e) => onCambio(e.target.value)}
            className={clasesCon(hayError)}
            placeholder={placeholder}
            aria-describedby={describedBy}
            aria-invalid={hayError}
            autoFocus={autoFocus}
            maxLength={maxLength}
            list={lista !== undefined && lista.length > 0 ? idLista : undefined}
            autoComplete="off"
          />
          {lista !== undefined && lista.length > 0 && (
            <datalist id={idLista}>
              {lista.map((opcion) => (
                <option key={opcion} value={opcion} />
              ))}
            </datalist>
          )}
        </>
      )}
    </Campo>
  );
}

// ---------------------------------------------------------------------------

interface PropsNumero {
  etiqueta: string;
  /** Solo para el lector de pantalla (Campo). */
  complemento?: string;
  valor: string;
  onCambio: (valor: string) => void;
  ayuda?: string;
  error?: string | null;
  placeholder?: string;
  autoFocus?: boolean;
  /** true = admite decimales, como 22.5 kg. */
  decimal?: boolean;
}

/**
 * Campo numérico. Se maneja como texto a propósito: convertirlo a número en
 * cada tecla impediría escribir "70." mientras se teclea "70.5".
 */
export function NumberField({
  etiqueta,
  complemento,
  valor,
  onCambio,
  ayuda,
  error,
  placeholder,
  autoFocus,
  decimal = true,
}: PropsNumero) {
  return (
    <Campo etiqueta={etiqueta} complemento={complemento} ayuda={ayuda} error={error}>
      {({ id, describedBy, hayError }) => (
        <input
          id={id}
          type="text"
          inputMode={decimal ? 'decimal' : 'numeric'}
          value={valor}
          onChange={(e) => {
            // Solo dígitos y un separador decimal; la coma se convierte en punto
            onCambio(
              decimal ? limpiarDecimal(e.target.value) : e.target.value.replace(/[^0-9]/g, ''),
            );
          }}
          className={`${clasesCon(hayError)} text-right tabular-nums`}
          placeholder={placeholder}
          aria-describedby={describedBy}
          aria-invalid={hayError}
          autoFocus={autoFocus}
          autoComplete="off"
        />
      )}
    </Campo>
  );
}

// ---------------------------------------------------------------------------

export interface OpcionSelect {
  valor: string;
  etiqueta: string;
}

interface PropsSelect {
  etiqueta: string;
  /** Solo para el lector de pantalla (Campo). */
  complemento?: string;
  valor: string;
  opciones: readonly OpcionSelect[];
  onCambio: (valor: string) => void;
  ayuda?: string;
  error?: string | null;
}

export function SelectField({
  etiqueta,
  complemento,
  valor,
  opciones,
  onCambio,
  ayuda,
  error,
}: PropsSelect) {
  return (
    <Campo etiqueta={etiqueta} complemento={complemento} ayuda={ayuda} error={error}>
      {({ id, describedBy, hayError }) => (
        <select
          id={id}
          value={valor}
          onChange={(e) => onCambio(e.target.value)}
          className={clasesCon(hayError)}
          aria-describedby={describedBy}
          aria-invalid={hayError}
        >
          {opciones.map((o) => (
            <option key={o.valor} value={o.valor}>
              {o.etiqueta}
            </option>
          ))}
        </select>
      )}
    </Campo>
  );
}

// ---------------------------------------------------------------------------

interface PropsContrasena {
  etiqueta: string;
  valor: string;
  onCambio: (valor: string) => void;
  /** 'new-password' al crearla, para que el iPhone ofrezca guardarla en Contraseñas. */
  autoComplete: 'new-password' | 'current-password';
  ayuda?: string;
  error?: string | null;
  autoFocus?: boolean;
}

/**
 * Contraseña con botón para verla, que ayuda a no equivocarse al escribirla
 * en el teléfono. No se guarda en ningún lado (Req. 6.19).
 */
export function PasswordField({
  etiqueta,
  valor,
  onCambio,
  autoComplete,
  ayuda,
  error,
  autoFocus,
}: PropsContrasena) {
  const [visible, setVisible] = useState(false);

  return (
    <Campo etiqueta={etiqueta} ayuda={ayuda} error={error}>
      {({ id, describedBy, hayError }) => (
        <div className="flex items-center gap-2">
          <input
            id={id}
            type={visible ? 'text' : 'password'}
            value={valor}
            onChange={(e) => onCambio(e.target.value)}
            className={`${clasesCon(hayError)} min-w-0 flex-1`}
            aria-describedby={describedBy}
            aria-invalid={hayError}
            autoComplete={autoComplete}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            autoFocus={autoFocus}
          />
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            aria-pressed={visible}
            className="inline-flex min-h-12 shrink-0 items-center gap-1.5 rounded-[13px] bg-marca-suave px-3 text-[13px] font-medium text-texto"
          >
            {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            {visible ? 'Ocultar' : 'Ver'}
          </button>
        </div>
      )}
    </Campo>
  );
}

// ---------------------------------------------------------------------------

interface PropsFecha {
  etiqueta: string;
  valor: string;
  onCambio: (valor: string) => void;
  ayuda?: string;
  error?: string | null;
  /** Primer día que se puede elegir, por ejemplo hoy. Quien guarda lo vuelve a revisar. */
  min?: string;
  /** Último día que se puede elegir, por ejemplo hoy para un pesaje. Quien guarda lo vuelve a revisar. */
  max?: string;
}

/** Fecha en formato AAAA-MM-DD, que es como se guarda en la base (design.md, sección 6). */
export function DateField({ etiqueta, valor, onCambio, ayuda, error, min, max }: PropsFecha) {
  return (
    <Campo etiqueta={etiqueta} ayuda={ayuda} error={error}>
      {({ id, describedBy, hayError }) => (
        <input
          id={id}
          type="date"
          value={valor}
          min={min}
          max={max}
          onChange={(e) => onCambio(e.target.value)}
          className={clasesCon(hayError)}
          aria-describedby={describedBy}
          aria-invalid={hayError}
        />
      )}
    </Campo>
  );
}
